import TextField, { BaseTextFieldProps } from "@mui/material/TextField";
import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState, useEffect, useMemo } from "react";
import Title, { InvalidTitleError } from "_shared/titles/title";
import { debounce } from "lodash";

const InputField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

interface TitleInputProps extends BaseTextFieldProps {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  disabled?: boolean;
  titleValue?: Title;
  label?: string;
  onChange?: (title?: Title, error?: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

export default function TitleInput(props: Readonly<TitleInputProps>) {
  const {
    idPrefix,
    className,
    variant,
    disabled,
    titleValue,
    label,
    onChange,
    onKeyDown,
    ...otherProps
  } = props;

  const [titleFieldText, setTitleFieldText] = useState<string>(titleValue?.value ?? '');
  const [titleFieldError, setTitleFieldError] = useState<string>('');

  useEffect(() => {
    setTitleFieldText(titleValue?.value ?? '');
  }, [titleValue]);

  function validateAndReport(titleValue?: string) {
    let title: Title | undefined;
    let newErrors: string = '';

    try {
      title = buildTitle(titleValue);
      setTitleFieldError('');
    } catch (error) {
      if (error instanceof InvalidTitleError) {
        newErrors = error.message;
        setTitleFieldError(newErrors);
      } else {
        throw error;
      }
    } finally {
      onChange?.(title, newErrors);
    }
  }

  const debouncedValidate = useMemo(() =>
    debounce((value: string) => validateAndReport(value), 500
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), []);

  useEffect(() => {
    return () => debouncedValidate.cancel();
  }, [debouncedValidate]);

  return (
    <InputField
      id={idPrefix ? idPrefix + "-title-input" : "title-input"}
      className={className}
      label={label ?? "Title"}
      onChange={(event) => {
        const value = event.target.value;
        setTitleFieldText(value);
        debouncedValidate(value);
      }}
      required
      disabled={disabled}
      value={titleFieldText}
      error={!!titleFieldError}
      helperText={titleFieldError}
      variant={variant ?? "outlined"}
      autoComplete="organization"
      onKeyDown={onKeyDown}
      {...otherProps}
    />
  );
}

function buildTitle(inputText?: string): Title | undefined {
  try {
    if (!inputText) throw new InvalidTitleError("Title is required");
    return new Title(inputText);
  } catch (error) {
    if (error instanceof InvalidTitleError) {
      throw error;
    }
  }
}
